"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Picture } from "@/components/Picture";
import { MapView } from "@/components/map/MapView";
import { BOOKMARK_KIND_KEYS, BOOKMARK_KINDS, bookmarkKey, type Bookmark, type BookmarkDetails, type BookmarkResponse } from "@/lib/bookmarks-shared";
import { copyText } from "@/lib/clipboard";
import { clearBookmarks, mergeBookmarks, removeBookmark, updateBookmarks, useBookmarks } from "./store";
import "@/components/travel/travel.css";
import "./bookmarks.css";

type Load = { state: "idle" | "loading" | "error" } | { state: "ready"; keys: string; data: BookmarkResponse };

function useDetails(list: Bookmark[] | null) {
  const keys = list?.map(bookmarkKey).join(",") ?? "";
  const [load, setLoad] = useState<Load>({ state: "idle" });
  useEffect(() => {
    if (!keys) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setLoad((l) => (l.state === "ready" ? l : { state: "loading" }));
      fetch("/api/merkliste", { method: "POST", body: keys, headers: { "content-type": "text/plain" }, signal: ctrl.signal, credentials: "omit" })
        .then((r) => (r.ok ? (r.json() as Promise<BookmarkResponse>) : Promise.reject(new Error(String(r.status)))))
        .then((data) => setLoad({ state: "ready", keys, data }))
        .catch(() => {
          if (!ctrl.signal.aborted) setLoad({ state: "error" });
        });
    }, 50);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [keys]);
  return load;
}

/** Eigene Merkliste aus dem Browser oder eine geteilte Liste aus dem Link (?l=…). */
export function BookmarkList({ shared }: { shared: Bookmark[] | null }) {
  const own = useBookmarks();
  const list = shared ?? own;
  const load = useDetails(list);
  const [note, setNote] = useState("");

  // Umbenannte Einträge und neue Titel in die eigene Liste übernehmen.
  const data = load.state === "ready" ? load.data : null;
  useEffect(() => {
    if (!data || shared) return;
    updateBookmarks(data.moved, new Map(data.items.map((i) => [i.key, i.title])));
  }, [data, shared]);

  const byKey = useMemo(() => new Map(data?.items.map((i) => [i.key, i]) ?? []), [data]);
  const ownKeys = new Set(own?.map(bookmarkKey) ?? []);

  if (list === null) return <p className="tempty">Merkliste wird geladen …</p>;

  if (list.length === 0) {
    return (
      <div className="bm-empty">
        <p>Noch nichts gemerkt. Tippe bei Orten, Routen und Beiträgen auf <strong>Merken</strong>, dann landen sie hier, mit Karte und einem Link zum Teilen.</p>
        <p>Die Merkliste wird nur in diesem Browser gespeichert, nicht auf unserem Server.</p>
        <p><Link className="tbutton" href="/orte">Orte entdecken</Link> <Link className="tbutton bm-ghost" href="/routen">Routen ansehen</Link></p>
      </div>
    );
  }

  // Einträge in gespeicherter Reihenfolge; ohne Antwort vom Server mit dem gemerkten Titel.
  const rows = list.map((b) => {
    const key = bookmarkKey(b);
    const movedTo = data?.moved[key];
    return { b, key, d: byKey.get(movedTo ?? key), gone: !!data?.missing.includes(key) };
  });
  const markers = rows.flatMap((r) => (r.d?.marker ? [r.d.marker] : []));
  const tracks = rows.flatMap((r) => (r.d?.track ? [r.d.track] : []));

  const share = async () => {
    const url = `${location.origin}/merkliste?l=${list.map(bookmarkKey).join(",")}`;
    if (navigator.share && window.isSecureContext) {
      try {
        await navigator.share({ title: "Meine Gran-Canaria-Merkliste", url });
        return;
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
      }
    }
    setNote((await copyText(url)) ? "Link kopiert. Wer ihn öffnet, sieht diese Liste und kann sie übernehmen." : `Kopieren nicht möglich. Link: ${url}`);
  };
  const missingShared = shared ? shared.filter((b) => !ownKeys.has(bookmarkKey(b))) : [];

  return (
    <>
      {shared && (
        <div className="bm-shared">
          <p>Das ist eine geteilte Merkliste mit {shared.length} {shared.length === 1 ? "Eintrag" : "Einträgen"}.</p>
          {missingShared.length > 0 ? (
            <button type="button" className="tbutton" onClick={() => {
              setNote(mergeBookmarks(missingShared) ? `${missingShared.length} ${missingShared.length === 1 ? "Eintrag" : "Einträge"} in deine Merkliste übernommen.` : "Konnte nicht gespeichert werden.");
            }}>In meine Merkliste übernehmen</button>
          ) : <p><strong>Alle Einträge sind schon auf deiner Merkliste.</strong></p>}
          <Link className="bm-link" href="/merkliste">Meine Merkliste öffnen</Link>
        </div>
      )}

      {!shared && (
        <div className="bm-actions">
          <button type="button" className="tbutton" onClick={share}>Liste teilen</button>
          <button type="button" className="tbutton bm-ghost" onClick={() => window.print()}>Drucken</button>
          <button type="button" className="tbutton bm-ghost" onClick={() => {
            if (confirm("Alle Einträge von der Merkliste entfernen?")) clearBookmarks();
          }}>Liste leeren</button>
        </div>
      )}
      <p className="bm-note" role="status">{note}</p>
      {load.state === "error" && <p className="bm-note">Die Details konnten gerade nicht geladen werden. Die Einträge sind trotzdem gespeichert.</p>}

      {(markers.length > 0 || tracks.length > 0) && (
        <MapView className="bm-map" mode="embed" label="Karte der gemerkten Orte und Routen" markers={markers} tracks={tracks} />
      )}

      {BOOKMARK_KIND_KEYS.map((k) => {
        const group = rows.filter((r) => r.b.k === k);
        if (!group.length) return null;
        return (
          <section key={k} className="tsection" aria-labelledby={`bm-${k}`}>
            <h2 id={`bm-${k}`}>{BOOKMARK_KINDS[k].plural} <span className="tpill-count">{group.length}</span></h2>
            <ul className="bm-list">
              {group.map(({ b, key, d, gone }) => (
                <Row key={key} b={b} d={d} gone={gone} onRemove={shared ? undefined : () => removeBookmark(key)} />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

function Row({ b, d, gone, onRemove }: { b: Bookmark; d?: BookmarkDetails; gone: boolean; onRemove?: () => void }) {
  const title = d?.title ?? b.t ?? b.s;
  return (
    <li className={`bm-item${gone ? " gone" : ""}`}>
      <div className="bm-thumb">
        {d?.media ? <Picture media={d.media} crop="1x1" sizes="96px" alt="" /> : <span className="tcard-ph" aria-hidden="true" />}
      </div>
      <div className="bm-body">
        {d?.kicker && <p className="tcard-kicker">{d.kicker}</p>}
        <h3>{gone ? title || b.s : <a href={d?.href ?? `${BOOKMARK_KINDS[b.k].path}${b.s}`}>{title || b.s}</a>}</h3>
        {gone ? <p className="bm-text">Nicht mehr verfügbar.</p> : d?.text && <p className="bm-text">{d.text}</p>}
      </div>
      {onRemove && (
        <button type="button" className="bm-remove" onClick={onRemove} aria-label={`${title || b.s} von der Merkliste entfernen`}>Entfernen</button>
      )}
    </li>
  );
}
